export type Field = { 
  key: string; 
  label: string; 
  type: 'text' | 'textarea' | 'select' | 'date' | 'number' | 'email' | 'tel' | 'file' | 'dependent-select'; 
  options?: string[] | Record<string, string[]>;
  required?: boolean;
  dependsOn?: string;
};

export type Section = { 
  key: string; 
  title: string; 
  multi?: boolean; 
  fields: Field[] 
};

// College/Institute and Department mapping
export const collegesDepartments = {
  "Akre Technical Institute": [
    "Information Technology",
    "Law Administration", 
    "Nursing",
    "Survey"
  ],
  "College of Education": [
    "Kurdish Language",
    "Arabic Language",
    "English Language",
    "Biology",
    "History", 
    "Mathematics",
    "Religious Studies"
  ],
  "Bardarash Technical Institute": [
    "Accounting",
    "Business Administration",
    "Computer Networks",
    "Information Technology",
    "Nursing"
  ],
  "Akre Technical College": [
    "Accounting",
    "Business Administration", 
    "Horticulture"
  ],
  "Technical College of Informatics-Akre": [
    "Artificial Intelligence",
    "Information Technology",
    "Network and Information Security"
  ],
  "Presidency": [
    "Civil Engineering and Project Management",
    "Nursing",
    "Presidency staff"
  ]
};

export const sections: Section[] = [
  { 
    key: 'personal_info', 
    title: 'Personal Information', 
    fields: [
      { key: 'name_en', label: 'Full Name (English)', type: 'text', required: true },
      { key: 'name_ar', label: 'Full Name (Arabic)', type: 'text' },
      { key: 'name_ku', label: 'Full Name (Kurdish)', type: 'text' },
      { key: 'title_en', label: 'Title (English)', type: 'text' },
      { key: 'title_ar', label: 'Title (Arabic)', type: 'text' },
      { key: 'title_ku', label: 'Title (Kurdish)', type: 'text' },
      { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female'] },
      { key: 'date_of_birth', label: 'Date of Birth', type: 'date', required: true },
      { key: 'nationality', label: 'Nationality', type: 'text' },
      { key: 'email', label: 'Email Address', type: 'email', required: true },
      { key: 'phone', label: 'Phone Number', type: 'tel', required: true }
    ]
  },
  {
    key: 'institution_info',
    title: 'Institution Information',
    fields: [
      { 
        key: 'college_institute', 
        label: 'College/Institute', 
        type: 'select', 
        options: Object.keys(collegesDepartments),
        required: true 
      },
      { 
        key: 'department', 
        label: 'Department', 
        type: 'dependent-select', 
        options: collegesDepartments,
        dependsOn: 'college_institute',
        required: true 
      },
      { key: 'specialization_en', label: 'Specialization (English)', type: 'text' },
      { key: 'specialization_ar', label: 'Specialization (Arabic)', type: 'text' },
      { key: 'specialization_ku', label: 'Specialization (Kurdish)', type: 'text' }
    ]
  },
  {
    key: 'biography',
    title: 'Biography',
    fields: [
      { key: 'biography_en', label: 'Biography (English)', type: 'textarea' },
      { key: 'biography_ar', label: 'Biography (Arabic)', type: 'textarea' },
      { key: 'biography_ku', label: 'Biography (Kurdish)', type: 'textarea' }
    ]
  },
  { 
    key: 'files', 
    title: 'Files Upload', 
    fields: [
      { key: 'photo', label: 'Profile Photo', type: 'file' },
      { key: 'cv_file', label: 'CV File', type: 'file' }
    ]
  },
  { 
    key: 'education', 
    title: 'Education', 
    multi: true, 
    fields: [
      { key: 'degree', label: 'Degree', type: 'select', options: ['High School', 'Diploma', 'BSc', 'MSc', 'PhD', 'Other'] },
      { key: 'field_of_study', label: 'Field of Study', type: 'text' },
      { key: 'university', label: 'University/Institution', type: 'text' },
      { key: 'country', label: 'Country', type: 'text' },
      { key: 'graduation_year', label: 'Graduation Year', type: 'number' },
      { key: 'gpa', label: 'GPA/Grade', type: 'text' }
    ]
  },
  {
    key: 'scientific_titles',
    title: 'Scientific Titles',
    multi: true,
    fields: [
      { 
        key: 'title', 
        label: 'Title', 
        type: 'select', 
        options: ['Assistant Lecturer', 'Lecturer', 'Assistant Professor', 'Professor'] 
      },
      { key: 'document_number', label: 'Document Number', type: 'text' },
      { key: 'year', label: 'Year', type: 'number' }
    ]
  },
  {
    key: 'teaching_subjects',
    title: 'Teaching Subjects',
    multi: true,
    fields: [
      { key: 'subject', label: 'Subject', type: 'text' },
      { 
        key: 'institution_type', 
        label: 'Institution Type', 
        type: 'select', 
        options: ['college', 'institute'] 
      },
      { 
        key: 'semester', 
        label: 'Semester', 
        type: 'dependent-select',
        options: {
          'institute': ['1', '2', '3', '4'],
          'college': ['1', '2', '3', '4', '5', '6', '7', '8']
        },
        dependsOn: 'institution_type'
      },
      { key: 'year', label: 'Year', type: 'select', options: Array.from({length: 30}, (_, i) => (new Date().getFullYear() - i).toString()) }
    ]
  },
  { 
    key: 'research_publications', 
    title: 'Research Publications', 
    multi: true, 
    fields: [
      { key: 'google_scholar_url', label: 'Google Scholar Profile URL', type: 'text' },
      { key: 'title', label: 'Publication Title', type: 'text' },
      { key: 'cited_by', label: 'Cited By', type: 'number' },
      { key: 'year', label: 'Publication Year', type: 'number' },
      { key: 'authors', label: 'Authors', type: 'text' },
      { key: 'journal', label: 'Journal/Conference', type: 'text' },
      { key: 'volume_issue', label: 'Volume/Issue', type: 'text' },
      { key: 'pages', label: 'Pages', type: 'text' },
      { key: 'doi', label: 'DOI', type: 'text' }
    ]
  },
  { 
    key: 'professional_memberships', 
    title: 'Professional Memberships', 
    multi: true, 
    fields: [
      { key: 'google_scholar_link', label: 'Google Scholar Link', type: 'text' },
      { key: 'research_gate_link', label: 'Research Gate Link', type: 'text' },
      { key: 'orcid_link', label: 'ORCID Link', type: 'text' },
      { key: 'linkedin_link', label: 'LinkedIn Link', type: 'text' },
      { 
        key: 'membership_role', 
        label: 'Professional Membership Role', 
        type: 'select', 
        options: ['Presenter', 'Reviewer', 'Participant', 'Attendance'] 
      },
      { key: 'organization', label: 'Organization Name', type: 'text' },
      { key: 'start_year', label: 'Start Year', type: 'number' },
      { key: 'end_year', label: 'End Year (if applicable)', type: 'number' }
    ]
  }
];